import * as React from "react";
import { listLocales, type AdminLocale } from "@/api/admin";
import { useAsync } from "@/lib/useAsync";

/**
 * The shop's languages, and which one a screen is looking at.
 *
 * Four screens read this list -- SEO, translations, assets, one article -- and
 * each wrote the same two lines: `useAsync(() => listLocales(), [])` and
 * `data?.locales ?? []`. Three then wrote the same effect to fall back to the
 * first language.
 *
 * THE `useMemo` IS NOT TIDYING. `?? []` builds a NEW array on every render, so
 * a screen putting `locales` in an effect's dependency list re-runs that effect
 * every single render -- and three of the four did exactly that. It settled
 * because the effects happen to be self-cancelling once a locale is chosen,
 * which is a bug that is invisible until somebody adds an effect that is not.
 * Only `TranslationsScreen` had the memo. Consolidating without it would have
 * promoted the version that was wrong.
 */
export interface LocaleList {
  locales: AdminLocale[];
  loading: boolean;
  error: string | null;
  reload: () => void;
}

export function useLocales(): LocaleList {
  const { data, loading, error, reload } = useAsync(() => listLocales(), []);
  const locales = React.useMemo(() => data?.locales ?? [], [data]);
  return { locales, loading, error, reload };
}

/**
 * Which language the screen is on, defaulting once the list arrives.
 *
 * `preferred` is for the article editor: opening a Spanish-only article on the
 * English tab shows an empty box beside a row that plainly has words in it, so
 * it asks for the language the article already HAS and falls back to the shop's
 * first. `sort` puts the shop's own language first, which is what somebody
 * opening one of these screens cold is most likely to want.
 *
 * The operator's choice wins from then on: the effect only fires while nothing
 * is chosen, so a list that reloads cannot drag them back to the top of it.
 */
export function useLocaleChoice(
  locales: AdminLocale[],
  preferred?: string | null,
): [string, (code: string) => void] {
  const [chosen, setChosen] = React.useState("");

  React.useEffect(() => {
    if (chosen || locales.length === 0) return;
    setChosen(preferred ?? locales[0]!.code);
  }, [locales, chosen, preferred]);

  return [chosen, setChosen];
}
